import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { CourseList } from "@/features/training/components/course-list"
import { DemoAnswerTools, DemoCanvas } from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

export function AIExplorerDocumentsPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-explorer.documents" })
  const { t: tResp } = useTranslation("", {
    keyPrefix: "training.ai-explorer.working-with-responses",
  })
  const { t: tCanvas } = useTranslation("", {
    keyPrefix: "training.ai-explorer.working-with-responses.canvas",
  })
  const list = <T = string,>(key: string) => ensureArray<T>(t(key, { returnObjects: true }))
  const listResp = <T = string,>(key: string) => ensureArray<T>(tResp(key, { returnObjects: true }))
  const listCanvas = <T = string,>(key: string) =>
    ensureArray<T>(tCanvas(key, { returnObjects: true }))

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.level")}</strong>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{t("learning_objectives.description")}</p>
        <CourseList data={list("learning_objectives.objectives")} />
      </CourseSection>

      {/* Working with Responses — merged section */}
      <CourseSection>
        <h2>{tResp("text_selection_menu.title")}</h2>
        <h3>{tResp("text_selection_menu.where.title")}</h3>
        <CourseList data={listResp("text_selection_menu.where.steps")} />
        <h3>{tResp("text_selection_menu.options.title")}</h3>
        <p>{parse(tResp("text_selection_menu.options.intro"))}</p>
        <CourseList data={listResp("text_selection_menu.options.items")} />
      </CourseSection>

      <CourseSection>
        <h2>{tResp("answer_tools.title")}</h2>
        <p>{parse(tResp("answer_tools.description"))}</p>
        <CourseList data={listResp("answer_tools.options")} />
        <div className="my-4">
          <DemoAnswerTools />
        </div>
      </CourseSection>

      <CourseSection>
        <h2>{tResp("break_out.title")}</h2>
        <h3>{tResp("break_out.what_it_does.title")}</h3>
        <p>{parse(tResp("break_out.what_it_does.description"))}</p>
        <CourseList data={listResp("break_out.what_it_does.situations")} />
      </CourseSection>

      {/* Documents — persistent workspace */}
      <CourseSection>
        <h2>{t("overview.title")}</h2>
        <p>{parse(t("overview.description"))}</p>
        <CourseList data={list("overview.points")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("sidebar_integration.title")}</h2>
        <CourseList data={list("sidebar_integration.points")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("create_entry_points.title")}</h2>
        <p>{parse(t("create_entry_points.description"))}</p>
        {list<{ title: string; steps: string[] }>("create_entry_points.paths").map(
          (path, index) => (
            <div key={index}>
              <h3>{path.title}</h3>
              <CourseList data={ensureArray<string>(path.steps)} />
            </div>
          )
        )}
      </CourseSection>

      <CourseSection>
        <h2>{t("documents_landing.title")}</h2>
        <p>{parse(t("documents_landing.description"))}</p>
        <CourseList data={list("documents_landing.points")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("editor_workflow.title")}</h2>
        <CourseList data={list("editor_workflow.steps")} />
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(t("editor_workflow.tip"))}</p>
        </blockquote>
      </CourseSection>

      <CourseSection>
        <h2>{tCanvas("title")}</h2>
        <h3>{tCanvas("where.title")}</h3>
        <CourseList data={listCanvas("where.location")} />
        <div className="my-4">
          <DemoCanvas />
        </div>

        <h3>{tCanvas("what_you_can_do.title")}</h3>
        <p>{parse(tCanvas("what_you_can_do.intro"))}</p>
        <CourseList data={listCanvas("what_you_can_do.features")} />
        <p>{tCanvas("what_you_can_do.also")}</p>
        <CourseList data={listCanvas("what_you_can_do.additional")} />
        <blockquote className="border-primary bg-primary/15 border-l-4 p-4">
          <p>{parse(tCanvas("what_you_can_do.remember"))}</p>
        </blockquote>

        <h3>{tCanvas("use_cases.title")}</h3>
        {listCanvas<{ title: string; points: string[] }>("use_cases.cases").map(
          (useCase, index) => (
            <div key={index}>
              <h4>{useCase.title}</h4>
              <CourseList data={ensureArray<string>(useCase.points)} />
            </div>
          )
        )}
      </CourseSection>

      <CourseSection>
        <h2>{t("use_cases.title")}</h2>
        {list<{ title: string; points: string[] }>("use_cases.cases").map((useCase, index) => (
          <div key={index}>
            <h3>{useCase.title}</h3>
            <CourseList data={ensureArray<string>(useCase.points)} />
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("exercise.title")}</h2>
        <p>{parse(t("exercise.intro"))}</p>
        <CourseList data={list("exercise.steps")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={list("summary.points")} />
        <p>{parse(t("summary.next_module"))}</p>
      </CourseSection>
    </CourseLayout>
  )
}
